import {useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {Catalog} from 'models/catalog';

/**
 * Interface describing the minimal i18n API needed for catalog translation
 */
interface I18nInstance {
  t: (key: string, options?: {defaultValue?: string}) => string;
}

/**
 * Hook para traducir catálogos dinámicamente
 * Retorna una función que traduce un catálogo al idioma actual
 *
 * @returns Función que recibe un catálogo y retorna el catálogo traducido
 *
 * @example
 * const translateCatalog = useTranslateCatalog();
 * const translatedCatalog = translateCatalog(catalog);
 * console.log(translatedCatalog.description); // "Vacaciones" en español, "Vacation" en inglés
 */
export const useTranslateCatalog = () => {
  const {t} = useTranslation();

  const translateCatalog = useCallback(
    (catalog: Catalog | null | undefined): Catalog | null => {
      if (!catalog) {
        return null;
      }

      const translationKey = `catalogs.${catalog.name}`;
      const translatedDescription = t(translationKey, {
        defaultValue: catalog.description,
      });

      return {
        ...catalog,
        description: translatedDescription,
        children:
          (catalog.children
            ?.map(child => translateCatalog(child))
            .filter(Boolean) as Catalog[]) || [],
      };
    },
    [t]
  );

  /**
   * Traduce un array de catálogos
   */
  const translateCatalogs = useCallback(
    (catalogs: Catalog[] | null | undefined): Catalog[] => {
      if (!catalogs || !Array.isArray(catalogs)) {
        return [];
      }

      return catalogs
        .map(catalog => translateCatalog(catalog))
        .filter(Boolean) as Catalog[];
    },
    [translateCatalog]
  );

  return {translateCatalog, translateCatalogs};
};

/**
 * Función standalone para traducir catálogos (sin hook)
 * Útil cuando no estás en componente React
 *
 * @param catalog Catálogo a traducir
 * @param i18n Instancia de i18n
 * @returns Catálogo traducido
 */
export const translateCatalogStandalone = (
  catalog: Catalog | null | undefined,
  i18n: I18nInstance
): Catalog | null => {
  if (!catalog) {
    return null;
  }

  const translationKey = `catalogs.${catalog.name}`;
  const translatedDescription = i18n.t(translationKey, {
    defaultValue: catalog.description,
  });

  return {
    ...catalog,
    description: translatedDescription,
    children:
      (catalog.children
        ?.map(child => translateCatalogStandalone(child, i18n))
        .filter(Boolean) as Catalog[]) || [],
  };
};
